import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchUnitUsers, fetchUsers, assignUnitToUser, removeUnitFromUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Modal } from "@/components/Modal";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const ASSIGNMENT_LABEL: Record<string, string> = {
  personal_owner: "owner",
  organization_grant: "org manager",
  delegated: "access",
  quarantined: "needs review",
  legacy: "unclassified",
};

function roleBadgeVariant(role: string) {
  switch (role) {
    case "admin": return "warning" as const;
    case "user": return "neutral" as const;
    default: return "outline" as const;
  }
}

interface UnitAssignmentProps {
  unitId: string;
}

export function UnitAssignment({ unitId }: UnitAssignmentProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const assignedQ = useQuery({
    queryKey: ["unit", unitId, "users"],
    queryFn: () => fetchUnitUsers(unitId),
    enabled: !!unitId,
  });

  const allUsersQ = useQuery({
    queryKey: ["users", "assignToUnit", searchDebounced],
    queryFn: () => fetchUsers({ q: searchDebounced || undefined, limit: 50 }),
    enabled: assignOpen,
  });

  const assignM = useMutation({
    mutationFn: (userId: number) => assignUnitToUser(unitId, userId),
    onSuccess: () => {
      toast.success("User assigned");
      assignedQ.refetch();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to assign user"),
  });

  const removeM = useMutation({
    mutationFn: (userId: number) => removeUnitFromUser(unitId, userId),
    onSuccess: () => {
      toast.success("User removed");
      assignedQ.refetch();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to remove user"),
  });

  const assignedIds = new Set((assignedQ.data?.users ?? []).map((u) => u.id));
  const unassignedUsers = (allUsersQ.data?.users ?? []).filter((u) => !assignedIds.has(u.id));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Assigned Users</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)}>
            + Assign
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {assignedQ.isLoading ? (
          <div className="text-sm opacity-60">Loading...</div>
        ) : assignedQ.isError ? (
          <div className="text-sm text-error">Failed to load users</div>
        ) : assignedQ.data?.users?.length ? (
          <div className="space-y-2">
            {assignedQ.data.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-card/50">
                <div>
                  <span className="font-medium">{u.username}</span>
                  <Badge variant={roleBadgeVariant(u.role)} className="ml-2 text-[11px] h-4.5 px-1.5">{u.role}</Badge>
                  {u.assignment_kind && (
                    <Badge variant="outline" className="ml-1 text-[11px] h-4.5 px-1.5"
                           data-testid="assignment-kind">
                      {ASSIGNMENT_LABEL[u.assignment_kind] ?? u.assignment_kind}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeM.mutate(u.id)}
                  disabled={removeM.isPending}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm opacity-60">No users assigned</div>
        )}
      </CardContent>

      <Modal open={assignOpen} onClose={() => { setAssignOpen(false); setSearch(""); }} title="Assign User to Unit">
        <div className="space-y-3">
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="space-y-2 max-h-64 overflow-auto">
            {allUsersQ.isLoading ? (
              <div className="text-sm opacity-60">Loading users...</div>
            ) : unassignedUsers.length === 0 ? (
              <div className="text-sm opacity-60">
                {searchDebounced ? "No users found" : "All users are already assigned"}
              </div>
            ) : (
              unassignedUsers.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-card/50">
                  <div>
                    <span className="font-medium">{u.username}</span>
                    <Badge variant={roleBadgeVariant(u.role)} className="ml-2 text-[11px] h-4.5 px-1.5">{u.role}</Badge>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => { assignM.mutate(u.id); setAssignOpen(false); setSearch(""); }}
                    disabled={assignM.isPending}
                  >
                    Assign
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>
    </Card>
  );
}
